import moment from "moment";

// Shared, pure calculation/formatting helpers for the Outstanding Report —
// extracted from OutstandingReport.js so the Auto Report Mail print page
// (PrintOutstandingReport.js) computes Received/Discount/SR-PR/Balance/O-D
// Days exactly the same way as the manual report, instead of drifting via
// a separate reimplementation.

export const parseData = (data) => {
  if (!data) return [];
  try {
    return typeof data === "string" ? JSON.parse(data) : data;
  } catch {
    return [];
  }
};

export const getSafeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

export const isCustomerReport = (reportType) => reportType?.includes("customer");
export const isSupplierReport = (reportType) => reportType?.includes("supplier");
export const isBrokerWise = (reportType) => reportType?.includes("broker");
export const isAgeWise = (reportType) => reportType?.includes("agewise");
export const isMonthWise = (reportType) => reportType?.includes("monthwise");

export const isAdvanceRow = (sale, reportType) => {
  if (sale?.status === "advance") return true;
  if (sale?.balType !== "openingBalance") return false;
  const upperType = sale?.type?.toUpperCase();
  return (isCustomerReport(reportType) && upperType === "C") ||
    (isSupplierReport(reportType) && upperType === "D");
};

export const isOpeningBalanceTop = (sale, reportType) => {
  if (sale?.balType !== "openingBalance") return false;
  return (reportType === "customer" && sale?.type === "D") ||
    (reportType === "supplier" && sale?.type === "C");
};

export const calcAmount = (sale, reportType) => {
  if (sale?.monthComplete) return 0;
  if (isAdvanceRow(sale, reportType)) return 0;
  return getSafeNumber(sale?.totalAmount);
};

export const calcReceivedAmount = (sale, reportType) => {
  if (sale?.monthComplete) return 0;
  if (sale?.transactionType === "CR") {
    return getSafeNumber(sale?.amount);
  }
  if (isAdvanceRow(sale, reportType)) {
    return getSafeNumber(sale?.balanceAmount || sale?.amount);
  }

  // Opening balance bills: amount/orignalBillAmount never change after creation,
  // and every match against the bill is reliably recorded in accountData —
  // so received = (bill total - opening pending amount) + everything matched since.
  // This avoids depending on outstandingVoucher.balanceAmount/recAmmount, which
  // aren't consistently populated across historical entries.
  if (sale?.balType === "openingBalance") {
    const baseline = getSafeNumber(sale?.totalAmount) - Math.abs(getSafeNumber(sale?.amount));
    const matched = getSafeNumber(
      sale?.accountData?.reduce((acc, account) => acc + getSafeNumber(account?.amount), 0)
    );
    return Math.max(0, baseline + matched);
  }

  if (sale?.amount && sale?.amount > 0 && sale?.totalAmount) {
    return getSafeNumber(sale?.totalAmount) - getSafeNumber(sale?.amount);
  }
  return getSafeNumber(
    sale?.accountData?.reduce((acc, account) => acc + getSafeNumber(account?.amount), 0)
  );
};

export const calcDiscountAmount = (sale) => {
  if (sale?.monthComplete) return 0;
  return getSafeNumber(
    sale?.accountData?.reduce((acc, account) => {
      if (account?.salesReference === "cashBank") {
        return acc + getSafeNumber(account?.discount);
      }
      return acc;
    }, 0)
  );
};

export const calcSrPrAmount = (sale, reportType) => {
  if (sale?.monthComplete) return 0;
  if (sale?.balType === "openingBalance") return 0;
  return getSafeNumber(
    sale?.accountData?.reduce((acc, account) => {
      const isCustomerSr = isCustomerReport(reportType) &&
        (account?.salesReference === "salesReturn" || account?.salesReference === "creditNote");
      const isSupplierPr = isSupplierReport(reportType) &&
        (account?.salesReference === "purchaseReturn" || account?.salesReference === "debitNotePurchase");
      return (isCustomerSr || isSupplierPr) ? acc + getSafeNumber(account?.amount) : acc;
    }, 0)
  );
};

export const calcBalanceAmount = (sale, reportType) => {
  if (sale?.monthComplete) return 0;

  // Use totalAmount as the base for all types, including openingBalance
  // to ensure consistency when subtracting receivedAmount.
  const totalAmount = getSafeNumber(sale?.totalAmount);
  const receivedAmount = calcReceivedAmount(sale, reportType);

  return Math.max(0, totalAmount - receivedAmount);
};

export const calcVoucherAmount = (sale) => {
  if (sale?.monthComplete) return 0;
  return getSafeNumber(
    sale?.accountData?.reduce((acc, account) => acc + getSafeNumber(account?.amount), 0)
  );
};

export const sumPartyField = (salesData, calcFn, reportType) => {
  if (!salesData?.length) return 0;
  return salesData.reduce((acc, sale) => acc + calcFn(sale, reportType), 0);
};

export const sumGrandTotalField = (filterReportData, calcFn, reportType) => {
  if (!filterReportData?.length) return 0;
  return filterReportData.reduce((reportSum, report) => {
    return reportSum + sumPartyField(report?.salesData, calcFn, reportType);
  }, 0);
};

export const sumBrokerPartyField = (salesDetails, calcFn, reportType) => {
  if (!salesDetails?.length) return 0;
  return salesDetails.reduce((acc, detail) => acc + calcFn(detail, reportType), 0);
};

export const sumBrokerGrandTotalField = (filterReportData, calcFn, reportType) => {
  if (!filterReportData?.length) return 0;
  return filterReportData.reduce((brokerSum, broker) => {
    const partiesSum = broker?.salesData?.reduce((partySum, party) => {
      return partySum + sumBrokerPartyField(party?.salesDetails, calcFn, reportType);
    }, 0) || 0;
    return brokerSum + partiesSum;
  }, 0);
};

export const calcOddDays = (sale) => {
  if (!sale?.invoiceDate) return "";
  const today = moment().startOf("day");
  const invoiceDate = moment(sale?.invoiceDate).startOf("day");
  const creditDays = getSafeNumber(sale?.creditDays);
  const dueDate = invoiceDate.clone().add(creditDays, "days");
  return today.diff(dueDate, "days");
};

export const formatNumber = (num) => getSafeNumber(num).toFixed(2);
export const formatDateDisplay = (date) => date ? moment(date).format("DD-MM-YYYY") : "";

// Mirrors OutstandingReport.js's own sortSalesData: opening-balance-top row
// first, ordinary bills by date, advance rows last, month-summary rows at
// the very end.
export const sortSalesData = (data, reportType) => {
  return [...data].sort((a, b) => {
    if (a?.monthComplete && b?.monthComplete) return 0;
    if (a?.monthComplete) return 1;
    if (b?.monthComplete) return -1;

    const aIsTop = isOpeningBalanceTop(a, reportType);
    const bIsTop = isOpeningBalanceTop(b, reportType);
    if (aIsTop && !bIsTop) return -1;
    if (!aIsTop && bIsTop) return 1;

    const aIsBottom = isAdvanceRow(a, reportType);
    const bIsBottom = isAdvanceRow(b, reportType);
    if (aIsBottom && !bIsBottom) return 1;
    if (!aIsBottom && bIsBottom) return -1;

    return moment(a.invoiceDate || a.date) - moment(b.invoiceDate || b.date);
  });
};

// Mirrors OutstandingReport.js's own processData(): the raw
// getOutstandingReportSP/getOutstandingReport1SP recordset is one row PER
// PARTY, each carrying salesData/openingData/advanceData as separate
// FOR-JSON-PATH JSON-string columns — this parses, merges (deduped by
// id/invoiceNo, matching the manual report so product-category names from
// duplicate rows get combined the same way), and sorts them into the one
// `salesData` bill-row array the calc functions above expect.
// includeAdvance mirrors the manual report's "With Advance" checkbox
// (checked by default there, so defaults to true here too).
export const processOutstandingPartyRow = (res, reportType, includeAdvance = true) => {
  if (!res?.salesData && !res?.brokerSalesData && !res?.openingData) return null;

  const salesData = parseData(res.salesData);
  const openingData = parseData(res.openingData);
  const advanceData = includeAdvance
    ? parseData(res.advanceData)?.map((d) => ({ ...d, balanceAmount: 0 })) || []
    : [];

  const mergedData = [...openingData, ...salesData, ...advanceData];
  const dataMap = new Map();

  mergedData.forEach((item) => {
    const key = item?.id || item?.invoiceNo;
    if (!dataMap.has(key)) {
      dataMap.set(key, { ...item });
    } else if (item?.ProdCategoryName) {
      const existingCategory = dataMap.get(key)?.ProdCategoryName;
      const categories = [existingCategory, item.ProdCategoryName]
        .filter((val) => val && val !== "null")
        .join(", ")
        .split(", ")
        .filter((v, i, arr) => arr.indexOf(v) === i);
      dataMap.get(key).ProdCategoryName = categories.join(", ");
    }
  });

  return {
    accountName: res.accountName,
    brokerId: res.brokerId,
    partyId: res.partyId,
    creditDays: res.creditDays,
    returnData: parseData(res.returnData),
    email: res.email,
    mobileNumber: res.mobileNumber,
    partyAddress: parseData(res.partyAddress),
    salesData: sortSalesData(Array.from(dataMap.values()), reportType),
  };
};
